/**
 * The preview stage and its frame loop (W2-02; ARCHITECTURE.md §7.3-7.5, §7.8; DESIGN.md §4.8-4.9).
 *
 * One stage per AppServices owns the GL canvas (the PreviewRenderer of W2-01a: a pure Phira render), the Canvas2D
 * overlay above it (Edit mode only) and a hover tooltip. Per frame:
 *     engine.frame(t, opts)  ->  renderer.render(list)  ->  overlay providers (FeatureModule.previewOverlays)
 * registered with the FrameScheduler as 'preview': drawn on demand while paused (document changes, selection, view,
 * seek, textures, skin, size) and every frame while playing (the shell makes the scheduler continuous).
 *
 * The stage is ATTACHED to a host element: the preview panel (docked or the Laptop mini window), Theater or Test
 * play. Attaching moves the same canvases (same GL context, nothing re-uploaded) into the newest host; detaching goes
 * back to the previous one. Theater and test play always draw the Clean render; test play takes its time, fades,
 * HUD slide and judgements from the test play controller and ignores the preview-only filters.
 *
 * Edit-mode pointer routing (ARCHITECTURE.md §7.4): providers are hit-tested in reverse `order`; the first hit owns
 * the pointer until release (onPointerDown / onPointerMove / onPointerUp). Without a captured pointer, moves are hover:
 * the cursor and the tooltip come from the topmost hits and every provider's onPointerMove sees `buttons = 0`.
 * Middle drag pans the view and Ctrl+wheel zooms it (Edit mode); the wheel alone scrolls time; a right click records
 * the line under the pointer for the 'preview.context' menu commands; a double click activates and frames a line.
 */

import type { AppServices } from '../../app/registry/types';
import type { FrameDrawList, FrameOptions, PreviewFilters } from '../../core/engine/types';
import type { LineId } from '../../core/model/types';
import type { Unsubscribe } from '../../core/util/types';
import { createPreviewRenderer } from '../../render/preview/PreviewRenderer';
import type {
  OverlayDrawContext,
  OverlayHit,
  OverlayPointerEvent,
  PreviewOverlayProvider,
  PreviewRenderer,
  RenderStats,
  TextureProvider,
} from '../../render/types';
import { PREVIEW_FRAME_ID } from '../../render/types';
import { builtinEffectsIn, sameBuiltinEffects } from './effectBadges';
import { previewFiltersOf } from './filters';
import { appKey, liveServices } from './live';
import { lineStatesAt, LineStatePool, makeOverlayContext } from './overlays/context';
import { worldToRpe } from './overlays/gizmoMath';
import { pickAt, activateLine } from './overlays/selection';
import { hitTooltip, frameInfo, lineShape, shapeCentre } from './overlays/shared';
import { readOverlayTheme, setOverlayTheme } from './overlays/theme';
import { bgDimOf, EXT_BG_DIM, EXT_SCREEN, readExt, screenAspect, screenShapeOf } from './prefs';
import { stageStateOf } from './stageState';
import {
  backingSize,
  centreOn,
  glPixelRatio,
  IDENTITY_VIEW,
  isIdentityView,
  overlayViewport,
  screenRect,
  zoomAt,
  type Rect,
  type StageView,
} from './stageGeometry';
import { testPlayOf, trackLengthOf } from './testPlayState';
import s from './Preview.module.css';

export type AttachKind = 'panel' | 'theater' | 'testPlay';

interface Attachment {
  readonly host: HTMLElement;
  readonly kind: AttachKind;
}

interface Layout {
  readonly cw: number;
  readonly ch: number;
  readonly dpr: number;
  readonly glDpr: number;
  readonly screen: Rect;
  readonly overlayW: number;
  readonly overlayH: number;
}

/** Wheel distance (px after deltaMode normalisation) of one time-scroll step. */
const WHEEL_STEP_PX = 60;

function devicePixelRatioOf(services: AppServices): number {
  const scale = services.settings.value.preview.renderScale;
  const dpr = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
  return scale === 'auto' ? Math.min(dpr, 2) : scale;
}

function normalizedDeltaY(e: WheelEvent, pageH: number): number {
  if (e.deltaMode === 1) return e.deltaY * 16;
  if (e.deltaMode === 2) return e.deltaY * pageH;
  return e.deltaY;
}

export class PreviewStage {
  readonly root: HTMLDivElement;
  readonly glCanvas: HTMLCanvasElement;
  readonly overlay: HTMLCanvasElement;
  private readonly tooltip: HTMLDivElement;
  private readonly services: AppServices;
  private renderer: PreviewRenderer | null = null;
  private readonly attachments: Attachment[] = [];
  private readonly offs: Unsubscribe[] = [];
  private offStore: Unsubscribe | null = null;
  private offTextures: Unsubscribe | null = null;
  private boundTextures: TextureProvider | null = null;
  private providers: readonly PreviewOverlayProvider[] | null = null;
  private list: FrameDrawList | null = null;
  private lastCtx: OverlayDrawContext | null = null;
  private readonly pool = new LineStatePool();
  private layout: Layout | null = null;
  private sizeDirty = true;
  private shaderErrorsKey = '';
  private resizeObserver: ResizeObserver | null = null;
  private capture: { readonly provider: PreviewOverlayProvider; readonly pointerId: number } | null = null;
  private pan: { readonly pointerId: number; readonly x: number; readonly y: number; readonly view: StageView } | null =
    null;
  private wheelAcc = 0;
  private lastDraw = -Infinity;
  private lastMode: 'clean' | 'edit' = 'clean';
  /** Engine the pooled draw list was filled by (a new engine starts a new list). */
  private listEngine: unknown = null;
  private themeDirty = true;
  /** CR-W3-03-5 (UX-030): skip the engine query and draw when the drawn content cannot have changed. */
  private contentDirty = true;
  /** Playhead time of the drawn list (a live easing preview's `timeOverride` counts as `t` moving). */
  private lastT: number | null = null;
  /** Document seq of the drawn list. */
  private lastRev = -1;
  private disposed = false;

  constructor(services: AppServices) {
    this.services = services;
    const doc = document;
    this.root = doc.createElement('div');
    this.root.className = s.stage ?? '';
    this.root.dataset.previewStage = '';
    // The stage handles the wheel itself (time scroll, zoom): the keyboard router leaves it alone.
    this.root.dataset.wheel = 'native';
    this.glCanvas = doc.createElement('canvas');
    this.glCanvas.className = s.glCanvas ?? '';
    this.glCanvas.setAttribute('aria-hidden', 'true');
    this.overlay = doc.createElement('canvas');
    this.overlay.className = s.overlayCanvas ?? '';
    this.overlay.setAttribute('aria-hidden', 'true');
    this.tooltip = doc.createElement('div');
    this.tooltip.className = s.stageTooltip ?? '';
    this.tooltip.setAttribute('role', 'tooltip');
    this.tooltip.hidden = true;
    this.root.append(this.glCanvas, this.overlay, this.tooltip);
    this.renderer = createPreviewRenderer(this.glCanvas);
    this.renderer.setSkin(services.skins.active);
    this.applyRenderScaleMode();
    this.offs.push(
      this.renderer.onContextRestored(() => {
        this.invalidate();
      }),
    );
    // Fonts, media, video seeks, deferred text or a new automatic scale: the last frame is stale.
    this.offs.push(
      this.renderer.onInvalidate(() => {
        this.invalidate();
      }),
    );
    this.offs.push(
      services.scheduler.add(PREVIEW_FRAME_ID, 'preview', (now) => {
        this.frame(now);
      }),
    );
    this.bindServices();
    this.offs.push(
      services.onChange(() => {
        this.providers = null;
        this.bindServices();
        this.invalidate();
      }),
    );
    this.offs.push(
      services.editor.subscribe((st, prev) => {
        if (st.preview.mode !== prev.preview.mode && st.preview.mode === 'clean') this.releaseCapture();
        // CR-W3-03-5 (UX-030): a timeline scroll/zoom alone does not change what the preview draws.
        const onlyTimeline =
          st.timeline !== prev.timeline &&
          st.preview === prev.preview &&
          st.filters === prev.filters &&
          st.activeLine === prev.activeLine &&
          st.selection === prev.selection &&
          st.hover === prev.hover &&
          st.focusedColumn === prev.focusedColumn;
        if (!onlyTimeline) this.invalidate();
      }),
    );
    this.offs.push(
      services.audio.transport.onChange(() => {
        this.invalidate();
      }),
    );
    this.offs.push(
      services.settings.subscribe(() => {
        // The theme attribute is applied by the shell in its own settings listener: read the tokens at the next frame.
        this.themeDirty = true;
        this.applyRenderScaleMode();
        this.invalidate();
      }),
    );
    this.offs.push(
      services.skins.subscribe((skin) => {
        this.renderer?.setSkin(skin);
        this.invalidate();
      }),
    );
    this.offs.push(
      testPlayOf(services).subscribe(() => {
        this.invalidate();
      }),
    );
    this.offs.push(
      stageStateOf(services).subscribe(() => {
        this.invalidate();
      }),
    );
    this.installPointer();
    if (typeof ResizeObserver === 'function') {
      this.resizeObserver = new ResizeObserver(() => {
        this.sizeDirty = true;
        this.request();
      });
      this.resizeObserver.observe(this.root);
    }
  }

  private bindServices(): void {
    this.offStore?.();
    this.offStore =
      this.services.store?.subscribe(() => {
        this.invalidate();
      }) ?? null;
    const textures = this.services.textures;
    if (textures !== this.boundTextures) {
      this.offTextures?.();
      this.offTextures = null;
      this.boundTextures = textures;
      if (textures !== null) {
        this.renderer?.setTextures(textures);
        this.offTextures = textures.onReady(() => {
          this.invalidate();
        });
      }
    }
  }

  /** settings.preview.renderScale 'auto' lets the renderer lower its scale on slow frames; a number fixes it. */
  private applyRenderScaleMode(): void {
    this.renderer?.setRenderScaleMode(this.services.settings.value.preview.renderScale === 'auto' ? 'auto' : 'fixed');
  }

  /** Custom shader failures go to the Problems panel (PH-33), once per change. */
  private reportShaderErrors(errors: RenderStats['shaderErrors']): void {
    const key = errors.map((e) => `${String(e.asset)}\u0000${e.log}`).join('\u0001');
    if (key === this.shaderErrorsKey) return;
    this.shaderErrorsKey = key;
    this.services.problems.reportShaderErrors?.(errors);
  }

  request(): void {
    if (!this.disposed) this.services.scheduler.request(PREVIEW_FRAME_ID);
  }

  /** The drawn content is stale (textures, theme, document, services...): redraw even at the same `t`. */
  private invalidate(): void {
    this.contentDirty = true;
    this.request();
  }

  /** The attachment the stage is shown in (the newest). */
  get attachment(): Attachment | null {
    return this.attachments[this.attachments.length - 1] ?? null;
  }

  get kind(): AttachKind | null {
    return this.attachment?.kind ?? null;
  }

  /** Shows the stage in `host` until the returned function is called (the newest attachment wins). */
  attach(host: HTMLElement, kind: AttachKind): () => void {
    const a: Attachment = { host, kind };
    this.attachments.push(a);
    this.mount();
    return () => {
      const i = this.attachments.indexOf(a);
      if (i < 0) return;
      this.attachments.splice(i, 1);
      this.releaseCapture();
      this.mount();
    };
  }

  private mount(): void {
    const top = this.attachment;
    if (top === null) {
      this.root.remove();
      return;
    }
    if (this.root.parentElement !== top.host) top.host.appendChild(this.root);
    this.themeDirty = true;
    this.sizeDirty = true;
    this.hideTooltip();
    this.request();
  }

  private overlayProviders(): readonly PreviewOverlayProvider[] {
    if (this.providers === null) {
      this.providers = [...this.services.registry.previewOverlays(this.services)].sort((a, b) => a.order - b.order);
    }
    return this.providers;
  }

  // -------------------------------------------------------------------------------------------------------------
  // View (Edit-mode zoom and pan)
  // -------------------------------------------------------------------------------------------------------------

  get view(): StageView {
    return stageStateOf(this.services).getSnapshot().view;
  }

  setView(v: StageView): void {
    stageStateOf(this.services).set({ view: v });
  }

  resetView(): void {
    this.setView(IDENTITY_VIEW);
  }

  /** Zoom by a factor about the container centre (toolbar buttons). */
  zoomBy(factor: number): void {
    const l = this.layout;
    if (l === null) {
      this.setView({ ...this.view, zoom: this.view.zoom * factor });
      return;
    }
    this.setView(zoomAt(this.view, factor, l.cw / 2, l.ch / 2, l.cw, l.ch));
  }

  /** Pans the view so a line's anchor is centred (double click "frame it"). */
  frameLine(line: LineId): void {
    const c = this.lastCtx;
    const l = this.layout;
    if (c === null || l === null) return;
    const st = frameInfo(c).states.get(line);
    if (st === undefined) return;
    const store = this.services.store;
    const [px, py] = shapeCentre(lineShape(c, st, store?.doc ?? null, store?.idx ?? null));
    const cssX = px / l.dpr - l.screen.x;
    const cssY = py / l.dpr - l.screen.y;
    let view = centreOn(this.view, l.screen, cssX, cssY, l.cw, l.ch);
    // Far outside the screen: zoom out until the anchor fits.
    const offX = Math.abs(cssX - l.screen.w / 2) / (l.screen.w / 2 || 1);
    const offY = Math.abs(cssY - l.screen.h / 2) / (l.screen.h / 2 || 1);
    const out = Math.max(offX, offY);
    if (out > 1.6) view = zoomAt(view, 1 / Math.min(4, out / 1.2), l.cw / 2, l.ch / 2, l.cw, l.ch);
    this.setView(view);
  }

  // -------------------------------------------------------------------------------------------------------------
  // Layout and frames
  // -------------------------------------------------------------------------------------------------------------

  private effectiveMode(): 'clean' | 'edit' {
    const a = this.attachment;
    if (a === null || a.kind !== 'panel') return 'clean';
    return this.services.editor.getState().preview.mode;
  }

  private computeLayout(): Layout | null {
    const a = this.attachment;
    if (a === null) return null;
    const cw = this.root.clientWidth;
    const ch = this.root.clientHeight;
    if (!(cw > 0) || !(ch > 0)) return null;
    const dpr = devicePixelRatioOf(this.services);
    const store = this.services.store;
    const pkg = store?.doc.pkg;
    const chartAr = pkg !== undefined && pkg.aspectRatio > 0 ? pkg.aspectRatio : 16 / 9;
    const shape = readExt(this.services.settings, EXT_SCREEN, screenShapeOf);
    const aspect = screenAspect(shape, chartAr, a.kind !== 'panel');
    const view = this.effectiveMode() === 'edit' ? this.view : IDENTITY_VIEW;
    const screen = screenRect(cw, ch, aspect, view);
    const glDpr = glPixelRatio(dpr, screen.w, screen.h);
    const [ow, oh] = backingSize(cw, ch, dpr);
    return { cw, ch, dpr, glDpr, screen, overlayW: ow, overlayH: oh };
  }

  /** Lays the canvases out for the current host size, screen shape, chart aspect and view (applied when changed). */
  private applyLayout(): Layout | null {
    const l = this.computeLayout();
    const prev = this.layout;
    this.layout = l;
    if (l === null) return null;
    const same =
      !this.sizeDirty &&
      prev !== null &&
      prev.cw === l.cw &&
      prev.ch === l.ch &&
      prev.dpr === l.dpr &&
      prev.glDpr === l.glDpr &&
      prev.screen.x === l.screen.x &&
      prev.screen.y === l.screen.y &&
      prev.screen.w === l.screen.w &&
      prev.screen.h === l.screen.h;
    this.sizeDirty = false;
    if (same) return l;
    this.renderer?.resize(l.screen.w, l.screen.h, l.glDpr);
    this.glCanvas.style.left = `${String(l.screen.x)}px`;
    this.glCanvas.style.top = `${String(l.screen.y)}px`;
    this.glCanvas.style.width = `${String(l.screen.w)}px`;
    this.glCanvas.style.height = `${String(l.screen.h)}px`;
    this.overlay.width = l.overlayW;
    this.overlay.height = l.overlayH;
    return l;
  }

  private frame(now: number): void {
    const a = this.attachment;
    const { store, engine } = this.services;
    if (a === null || store === null || engine === null || this.renderer === null || this.disposed) return;
    const settings = this.services.settings.value;
    const transport = this.services.audio.transport;
    const cap = settings.preview.fpsCap;
    if (cap > 0 && transport.state.playing && now - this.lastDraw < 1000 / cap - 1) return;
    const mode = this.effectiveMode();
    if (mode !== this.lastMode) {
      this.lastMode = mode;
      this.sizeDirty = true;
      this.contentDirty = true;
      this.hideTooltip();
    }
    const st = this.services.editor.getState();
    const testPlay = a.kind === 'testPlay' ? testPlayOf(this.services) : null;
    const tp = testPlay?.active === true ? testPlay.frame(now) : null;
    const filters: PreviewFilters = tp?.filters ?? previewFiltersOf(st.filters, st.activeLine);
    // A live preview (easing / templates) may loop a span without moving the playhead (PreviewUiState.timeOverride).
    const t = tp?.t ?? st.preview.timeOverride ?? transport.chartTime();
    // CR-W3-03-5 (UX-030): skip the engine query and draw while the playhead time is unchanged and nothing is
    // dirty (document, selection, view, textures, skin, size all go through `invalidate`).
    if (
      tp === null &&
      !this.contentDirty &&
      !this.sizeDirty &&
      !this.themeDirty &&
      t === this.lastT &&
      store.seq === this.lastRev
    ) {
      return;
    }
    const l = this.applyLayout();
    if (l === null) return;
    this.lastDraw = now;
    if (this.themeDirty) {
      this.themeDirty = false;
      setOverlayTheme(readOverlayTheme(this.root));
    }
    if (this.listEngine !== engine) {
      this.listEngine = engine;
      this.list = null;
    }
    const glW = Math.max(1, Math.round(l.screen.w * l.glDpr));
    const glH = Math.max(1, Math.round(l.screen.h * l.glDpr));
    const opts: FrameOptions = {
      winW: glW,
      winH: glH,
      player: st.preview.player,
      skin: this.services.skins.active.metrics,
      resAlpha: tp?.resAlpha ?? 1,
      hudProgress: tp?.hudProgress ?? 1,
      showHud: tp !== null ? true : st.preview.showHud,
      showEffects: true,
      showVideos: true,
      rate: transport.state.rate,
      trackLengthSec: trackLengthOf(this.services),
      filters,
      ghosts: mode === 'edit' && st.preview.hiddenGhosts,
      judge: tp?.judge ?? null,
      // Phira player "background dim" emulation (a preview preference, never written to the chart).
      backgroundDimOverride: readExt(this.services.settings, EXT_BG_DIM, bgDimOf),
      // The legacy HUD layout measures with the preview's own fonts.
      measureText: this.renderer.measureHudText,
    };
    let list: FrameDrawList;
    try {
      list = engine.frame(t, opts, this.list ?? undefined);
    } catch (err) {
      console.error('[preview] engine.frame failed', err);
      return;
    }
    this.list = list;
    const stage = stageStateOf(this.services);
    const effects = builtinEffectsIn(list);
    if (!sameBuiltinEffects(effects, stage.getSnapshot().builtinEffects)) stage.set({ builtinEffects: effects });
    if (!this.renderer.contextLost) {
      const stats = this.renderer.render(list, engine, { msaa: st.preview.player.msaa, effects: true, videos: true });
      this.reportShaderErrors(stats.shaderErrors);
    }
    this.drawOverlay(mode, l, list, t, filters);
    this.lastT = t;
    this.lastRev = store.seq;
    this.contentDirty = false;
    if (tp?.animating === true) this.request();
  }

  private drawOverlay(
    mode: 'clean' | 'edit',
    l: Layout,
    list: FrameDrawList,
    t: number,
    filters: PreviewFilters,
  ): void {
    const ctx = this.overlay.getContext('2d');
    if (ctx === null) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.overlay.width, this.overlay.height);
    const engine = this.services.engine;
    if (this.attachment?.kind !== 'panel' || engine === null) {
      this.lastCtx = null;
      return;
    }
    // The context also serves Clean mode (right-click target, double click); only Edit mode draws the overlay.
    const vp = overlayViewport(list.viewport, l.screen, l.glDpr, l.dpr, l.overlayW, l.overlayH);
    const lines = this.pool.copy(lineStatesAt(engine, t, filters, list.viewport.ar));
    const c = makeOverlayContext({ ctx, viewport: vp, t, list, lines, engine, dpr: l.dpr });
    this.lastCtx = c;
    if (mode !== 'edit') return;
    for (const p of this.overlayProviders()) {
      try {
        ctx.save();
        p.draw(c);
      } catch (err) {
        console.error(`[preview] overlay ${p.id} failed`, err);
      } finally {
        ctx.restore();
      }
    }
  }

  // -------------------------------------------------------------------------------------------------------------
  // Pointer routing
  // -------------------------------------------------------------------------------------------------------------

  private pointerEvent(e: PointerEvent | MouseEvent, c: OverlayDrawContext): OverlayPointerEvent {
    const r = this.overlay.getBoundingClientRect();
    const dpr = this.layout?.dpr ?? 1;
    const px = (e.clientX - r.left) * dpr;
    const py = (e.clientY - r.top) * dpr;
    return {
      px,
      py,
      world: c.pxToWorld(px, py),
      buttons: e.buttons,
      shift: e.shiftKey,
      ctrl: e.ctrlKey || e.metaKey,
      alt: e.altKey,
    };
  }

  /** Topmost hit among the providers (reverse order), and the first one carrying a tooltip. */
  private hitAt(
    c: OverlayDrawContext,
    px: number,
    py: number,
  ): { readonly top: { p: PreviewOverlayProvider; hit: OverlayHit } | null; readonly tip: OverlayHit | null } {
    let top: { p: PreviewOverlayProvider; hit: OverlayHit } | null = null;
    let tip: OverlayHit | null = null;
    const ps = this.overlayProviders();
    for (let i = ps.length - 1; i >= 0; i--) {
      const p = ps[i];
      if (p?.hitTest === undefined) continue;
      let hit: OverlayHit | null = null;
      try {
        hit = p.hitTest(c, px, py);
      } catch (err) {
        console.error(`[preview] overlay ${p.id} hitTest failed`, err);
      }
      if (hit === null) continue;
      top ??= { p, hit };
      if (hitTooltip(hit) !== null) {
        tip = hit;
        break;
      }
    }
    return { top, tip };
  }

  private releaseCapture(): void {
    const cap = this.capture;
    this.capture = null;
    this.pan = null;
    if (cap !== null && this.lastCtx !== null) {
      try {
        cap.provider.onPointerUp?.(this.lastCtx, {
          px: -1,
          py: -1,
          world: [0, 0],
          buttons: 0,
          shift: false,
          ctrl: false,
          alt: false,
        });
      } catch {
        // A provider that fails on release keeps nothing.
      }
    }
  }

  private showTooltip(text: string, cssX: number, cssY: number): void {
    this.tooltip.textContent = text;
    this.tooltip.hidden = false;
    this.tooltip.style.left = `${String(cssX + 14)}px`;
    this.tooltip.style.top = `${String(cssY + 16)}px`;
  }

  private hideTooltip(): void {
    this.tooltip.hidden = true;
  }

  private installPointer(): void {
    const el = this.overlay;
    const onDown = (e: PointerEvent): void => {
      if (this.attachment?.kind !== 'panel') return;
      const region = this.root.closest<HTMLElement>('[data-region]');
      region?.focus({ preventScroll: true });
      if (this.effectiveMode() !== 'edit') return;
      const c = this.lastCtx;
      if (c === null) return;
      if (e.button === 1) {
        e.preventDefault();
        el.setPointerCapture(e.pointerId);
        this.pan = { pointerId: e.pointerId, x: e.clientX, y: e.clientY, view: this.view };
        return;
      }
      if (e.button !== 0) return;
      const ev = this.pointerEvent(e, c);
      const { top } = this.hitAt(c, ev.px, ev.py);
      if (top === null) return;
      this.hideTooltip();
      this.capture = { provider: top.p, pointerId: e.pointerId };
      el.setPointerCapture(e.pointerId);
      el.style.cursor = top.hit.cursor;
      try {
        top.p.onPointerDown?.(c, top.hit, ev);
      } catch (err) {
        console.error(`[preview] overlay ${top.p.id} failed`, err);
      }
      this.request();
    };
    const onMove = (e: PointerEvent): void => {
      const pan = this.pan;
      if (pan?.pointerId === e.pointerId) {
        this.setView({ ...pan.view, panX: pan.view.panX + e.clientX - pan.x, panY: pan.view.panY + e.clientY - pan.y });
        return;
      }
      if (this.attachment?.kind !== 'panel' || this.effectiveMode() !== 'edit') return;
      const c = this.lastCtx;
      if (c === null) return;
      const ev = this.pointerEvent(e, c);
      const cap = this.capture;
      if (cap?.pointerId === e.pointerId) {
        try {
          cap.provider.onPointerMove?.(c, ev);
        } catch (err) {
          console.error(`[preview] overlay ${cap.provider.id} failed`, err);
        }
        return;
      }
      if (cap !== null) return;
      const { top, tip } = this.hitAt(c, ev.px, ev.py);
      el.style.cursor = top?.hit.cursor ?? 'default';
      const ref = hitTooltip(tip);
      const r = el.getBoundingClientRect();
      if (ref === null) this.hideTooltip();
      else this.showTooltip(this.services.i18n.tr(ref), e.clientX - r.left, e.clientY - r.top);
      const hover = { ...ev, buttons: 0 };
      for (const p of this.overlayProviders()) {
        try {
          p.onPointerMove?.(c, hover);
        } catch (err) {
          console.error(`[preview] overlay ${p.id} failed`, err);
        }
      }
    };
    const onUp = (e: PointerEvent): void => {
      if (this.pan?.pointerId === e.pointerId) {
        this.pan = null;
        return;
      }
      const cap = this.capture;
      if (cap?.pointerId !== e.pointerId) return;
      this.capture = null;
      const c = this.lastCtx;
      if (c !== null) {
        try {
          cap.provider.onPointerUp?.(c, this.pointerEvent(e, c));
        } catch (err) {
          console.error(`[preview] overlay ${cap.provider.id} failed`, err);
        }
      }
      if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
      this.request();
    };
    const onLeave = (): void => {
      this.hideTooltip();
      const c = this.lastCtx;
      if (c === null || this.capture !== null) return;
      const away: OverlayPointerEvent = {
        px: -1e9,
        py: -1e9,
        world: [1e9, 1e9],
        buttons: 0,
        shift: false,
        ctrl: false,
        alt: false,
      };
      for (const p of this.overlayProviders()) {
        try {
          p.onPointerMove?.(c, away);
        } catch {
          // Hover bookkeeping only.
        }
      }
    };
    const onWheel = (e: WheelEvent): void => {
      if (this.attachment?.kind !== 'panel') return;
      e.preventDefault();
      if ((e.ctrlKey || e.metaKey) && this.effectiveMode() === 'edit') {
        const r = this.root.getBoundingClientRect();
        const l = this.layout;
        if (l === null) return;
        const factor = Math.exp(-normalizedDeltaY(e, l.ch) * 0.0015);
        this.setView(zoomAt(this.view, factor, e.clientX - r.left, e.clientY - r.top, l.cw, l.ch));
        return;
      }
      if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
      this.wheelAcc += normalizedDeltaY(e, this.layout?.ch ?? 600);
      while (Math.abs(this.wheelAcc) >= WHEEL_STEP_PX) {
        const up = this.wheelAcc < 0;
        this.wheelAcc += up ? WHEEL_STEP_PX : -WHEEL_STEP_PX;
        // Time flows up: wheel up moves later, as in the lanes.
        void this.services.registry.runAny(up ? 'playback.seekForward' : 'playback.seekBack', undefined, 'gesture');
      }
    };
    const onDouble = (e: MouseEvent): void => {
      if (this.attachment?.kind !== 'panel' || this.effectiveMode() !== 'edit') return;
      const c = this.lastCtx;
      if (c === null) return;
      const ev = this.pointerEvent(e, c);
      const pick = pickAt(this.services, c, ev.px, ev.py);
      if (pick.kind !== 'line') {
        if (pick.kind === 'empty' && !isIdentityView(this.view)) this.resetView();
        return;
      }
      const line = pick.lines[0];
      if (line === undefined) return;
      activateLine(this.services, line, 'replace');
      this.frameLine(line);
    };
    const onContext = (e: MouseEvent): void => {
      const c = this.lastCtx;
      const state = stageStateOf(this.services);
      if (c === null) {
        state.set({ contextTarget: null });
        return;
      }
      const ev = this.pointerEvent(e, c);
      const pick = pickAt(this.services, c, ev.px, ev.py);
      const line = pick.kind === 'line' ? (pick.lines[0] ?? null) : null;
      const ar = c.viewport.ar;
      state.set({
        contextTarget: { line, world: ev.world, rpe: worldToRpe(ev.world[0], ev.world[1], ar), at: Date.now() },
      });
    };
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    el.addEventListener('pointerleave', onLeave);
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('dblclick', onDouble);
    el.addEventListener('contextmenu', onContext);
    this.offs.push(() => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      el.removeEventListener('pointerleave', onLeave);
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('dblclick', onDouble);
      el.removeEventListener('contextmenu', onContext);
    });
  }

  /** The overlay context of the last Edit-mode frame (tests, context commands). */
  get frameContext(): OverlayDrawContext | null {
    return this.lastCtx;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.releaseCapture();
    for (const off of this.offs) off();
    this.offStore?.();
    this.offTextures?.();
    this.resizeObserver?.disconnect();
    this.renderer?.dispose();
    this.renderer = null;
    this.root.remove();
  }
}

const stages = new WeakMap<object, PreviewStage>();

/** The preview stage of an app (created on first use, in the browser, over the live services). */
export function previewStageOf(services: AppServices): PreviewStage {
  const key = appKey(services);
  let st = stages.get(key);
  if (st === undefined) {
    st = new PreviewStage(liveServices(services));
    stages.set(key, st);
  }
  return st;
}

/** The stage when it exists (no creation). */
export function peekPreviewStage(services: AppServices): PreviewStage | null {
  return stages.get(appKey(services)) ?? null;
}

/** Disposes the stage of an app (module teardown). */
export function disposePreviewStage(services: AppServices): void {
  const key = appKey(services);
  stages.get(key)?.dispose();
  stages.delete(key);
}
